import { apiService } from './api';

/**
 * Purchase Service
 * Handles game purchase operations
 */

export interface PurchaseInitiateResponse {
  success: boolean;
  data?: {
    purchase_id: string;
    client_secret: string;
    payment_intent_id: string;
    amount: number;
    currency: string;
  };
  message?: string;
}

export interface PurchaseHistoryResponse {
  success: boolean;
  data?: {
    purchases: Array<{
      id: string;
      game_id: string;
      game_name: string;
      game_icon: string | null;
      amount: number;
      currency: string;
      status: string;
      payment_provider: string;
      purchased_at: string;
    }>;
    count: number;
  };
  message?: string;
}

export interface LibraryResponse {
  success: boolean;
  data?: {
    games: Array<any>; // Game type
    count: number;
  };
  message?: string;
}

class PurchaseService {
  /**
   * Initiate a game purchase
   * @param gameId - Game UUID
   * @returns Promise with Stripe client secret
   */
  async initiatePurchase(gameId: string): Promise<PurchaseInitiateResponse> {
    try {
      const response = await apiService.post<PurchaseInitiateResponse>(
        `/user/purchases/${gameId}`,
        {}
      );
      return response.data;
    } catch (error: any) {
      console.error('[PurchaseService] Failed to initiate purchase:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to initiate purchase'
      };
    }
  }

  /**
   * Get user's purchase history
   * @param limit - Max number of records
   * @returns Promise with purchase history
   */
  async getPurchaseHistory(limit: number = 50): Promise<PurchaseHistoryResponse> {
    try {
      const response = await apiService.get<PurchaseHistoryResponse>(
        `/user/purchases?limit=${limit}`
      );
      return response.data;
    } catch (error: any) {
      console.error('[PurchaseService] Failed to fetch purchase history:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch purchase history'
      };
    }
  }

  /**
   * Get user's library (owned games)
   * @returns Promise with owned games
   */
  async getUserLibrary(): Promise<LibraryResponse> {
    try {
      const response = await apiService.get<LibraryResponse>('/user/library');
      return response.data;
    } catch (error: any) {
      console.error('[PurchaseService] Failed to fetch library:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch library'
      };
    }
  }
}

export const purchaseService = new PurchaseService();
export default purchaseService;
